import React, { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./Modal.css";

/**
 * Modal partagé.
 *
 * Props :
 *  - open / isOpen / visible / show : ouverture (alias rétro-compatibles)
 *  - onClose : callback de fermeture
 *  - title, children
 *  - closeOnOverlayClick (def. false) : un clic à CÔTÉ ne ferme PAS la fenêtre.
 *      Évite la perte de saisie dans les formulaires (Ajouter trajet/bus/chauffeur…).
 *      Fermeture possible via X, ESC, ou les boutons Annuler du contenu.
 *  - confirmOnClose : si truthy, demande confirmation avant de fermer (X/ESC/overlay).
 *      Passer une chaîne pour un message personnalisé, ou `true` pour le message par défaut.
 *      Brancher sur l'état "dirty" du formulaire → "Voulez-vous quitter sans enregistrer ?".
 */
const Modal = ({
  open, onClose, title, children,
  isOpen, visible, show,
  closeOnOverlayClick = false,
  confirmOnClose = false,
  size = "medium", // "medium" (def.) | "large" | "xlarge"
}) => {
  const { t } = useTranslation("common");
  const isModalOpen = open || isOpen || visible || show || false;

  // Fermeture centralisée : applique la confirmation si des données risquent d'être perdues.
  const requestClose = useCallback(() => {
    if (confirmOnClose) {
      const msg = typeof confirmOnClose === "string"
        ? confirmOnClose
        : "Voulez-vous quitter sans enregistrer ? Les modifications seront perdues.";
      if (!window.confirm(msg)) return;
    }
    if (onClose) onClose();
  }, [confirmOnClose, onClose]);

  // Bloque le scroll du body + ferme sur ESC (jamais sur clic extérieur par défaut)
  useEffect(() => {
    if (!isModalOpen) return undefined;

    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") requestClose(); };
    document.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = "auto";
      document.removeEventListener("keydown", onKey);
    };
  }, [isModalOpen, requestClose]);

  if (!isModalOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={closeOnOverlayClick ? requestClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
    >
      <div
        className={`modal-box ${size === "large" ? "modal-box-large" : size === "xlarge" ? "modal-box-xlarge" : "modal-box-compact"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close-x" onClick={requestClose} aria-label={t('btn.close')}>✖</button>

        {title && <h3 className="modal-title">{title}</h3>}

        <div className="modal-content modal-content-scrollable">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
